import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent } from '../../../components/ui/Card.js';
import { Button } from '../../../components/ui/Button.js';
import { StatusBadge } from '../../../components/ui/StatusBadge.js';
import { Money } from '../../../components/ui/Money';
import { Search, ShieldAlert } from 'lucide-react';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale';
import { useArrearsReport } from '../../../features/reports/client';
import { useSchemes } from '../../../features/schemes/network/schemes.api';
import { FormField, Input } from '../../../components/ui/FormField';
import { TableSkeleton } from '../../../components/ui/Skeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import { PageHeader } from '../../../components/layout/PageHeader';

export const Route = createFileRoute('/_auth/reports/arrears')({
 component: ArrearsDashboardPage,
});

interface DefaultRecord {
 id: string;
 customerName: string;
 groupCode: string;
 ticketNumber: string;
 overdueInstallments: number;
 unpaidPaise: number;
 interestFinesPaise: number;
 lastPaymentDate: string;
 agingBracket: '30_DAYS' | '60_DAYS' | '90_PLUS_DAYS';
}

function ArrearsDashboardPage() {
  const [selectedGroup, setSelectedGroup] = React.useState('');
  const [query, setQuery] = React.useState('');
  const { data = [], isLoading, isError, error, refetch } = useArrearsReport({ query: query || undefined, group: selectedGroup || undefined });
  const defaulters = data as DefaultRecord[];
  const { data: schemesResponse } = useSchemes({ limit: 100 });
  const schemeOptions = schemesResponse?.data ?? [];

  const filteredDefaulters = defaulters.filter(d => {
    const matchesQuery = d.customerName.toLowerCase().includes(query.toLowerCase()) ||
      d.ticketNumber.toLowerCase().includes(query.toLowerCase()) ||
      d.groupCode.toLowerCase().includes(query.toLowerCase());
    const matchesGroup = !selectedGroup || d.groupCode === selectedGroup;
    return matchesQuery && matchesGroup;
  });

  const totalPaiseDue30 = filteredDefaulters.filter(d => d.agingBracket === '30_DAYS').reduce((acc, d) => acc + d.unpaidPaise, 0);
  const totalPaiseDue60 = filteredDefaulters.filter(d => d.agingBracket === '60_DAYS').reduce((acc, d) => acc + d.unpaidPaise, 0);
  const totalPaiseDue90 = filteredDefaulters.filter(d => d.agingBracket === '90_PLUS_DAYS').reduce((acc, d) => acc + d.unpaidPaise, 0);
  const grandTotalArrears = totalPaiseDue30 + totalPaiseDue60 + totalPaiseDue90;

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader title="Arrears Dashboard" subtitle="Monitor overdue instalments and subscriber arrears across all schemes." />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl">
        <Card className="shadow-sm">
          <CardContent className="p-5 border-l-4 border-success-500">
            <span className="text-caption text-text-muted font-bold uppercase tracking-wider">30 Days Bracket</span>
            <h3 className="text-xl font-bold text-text-primary mt-1">
              <Money paise={totalPaiseDue30} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
            </h3>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-5 border-l-4 border-warning-500">
            <span className="text-caption text-text-muted font-bold uppercase tracking-wider">60 Days Bracket</span>
            <h3 className="text-xl font-bold text-text-primary mt-1">
              <Money paise={totalPaiseDue60} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
            </h3>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-5 border-l-4 border-danger-500">
            <span className="text-caption text-text-muted font-bold uppercase tracking-wider">90+ Days NPA Bracket</span>
            <h3 className="text-xl font-bold text-text-primary mt-1">
              <Money paise={totalPaiseDue90} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
            </h3>
          </CardContent>
        </Card>

        <Card className="shadow-sm bg-danger-bg/20 border-danger-bg">
          <CardContent className="p-5">
            <span className="text-caption text-danger-text font-bold uppercase tracking-wider">Total Delinquent Arrears</span>
            <h3 className="text-xl font-bold text-danger-text mt-1">
              <Money paise={grandTotalArrears} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
            </h3>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4 max-w-5xl">
        <div className="flex flex-wrap gap-4 items-center">
          <FormField label="Search" className="max-w-sm flex-1">
            <Input
              startIcon={<Search />}
              placeholder="Search delinquent subscriber, ticket..."
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </FormField>

          <div className="flex gap-2">
            <select
              value={selectedGroup}
              onChange={e => setSelectedGroup(e.target.value)}
              className="h-9 px-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500"
            >
              <option value="">All Chit Schemes</option>
              {schemeOptions.map(s => (
                <option key={s.id} value={s.schemeNumber}>{s.schemeNumber}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
          {isLoading ? (
            <TableSkeleton columns={7} rows={5} />
          ) : isError ? (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-danger-600">Failed to load arrears report</p>
              <p className="text-xs text-text-muted mt-1">{error?.message || 'Unknown error'}</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            </div>
          ) : filteredDefaulters.length === 0 ? (
            <EmptyState
              title="No arrears found"
              description={query || selectedGroup ? 'No records match your filters.' : 'No delinquent subscribers found.'}
              icon={<ShieldAlert className="w-8 h-8" />}
            />
          ) : (
            <table className="w-full border-collapse text-left text-sm">
              <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                <tr>
                  <th className="p-4">Subscriber Info</th>
                  <th className="p-4">Group Mapped</th>
                  <th className="p-4">Unpaid Installments</th>
                  <th className="p-4">Total Arrears</th>
                  <th className="p-4">Interest Fines</th>
                  <th className="p-4">Aging Class</th>
                  <th className="p-4">Last Payment Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-text-primary">
                {filteredDefaulters.map(d => (
                  <tr key={d.id} className="hover:bg-surface-2 transition-colors">
                    <td className="p-4">
                      <div className="flex flex-col">
                        <span className="font-semibold text-text-primary">{d.customerName}</span>
                        <span className="font-mono text-xs text-text-muted mt-0.5">{d.ticketNumber}</span>
                      </div>
                    </td>
                    <td className="p-4 font-bold">{d.groupCode}</td>
                    <td className="p-4 font-mono text-center font-bold text-danger-600">{d.overdueInstallments} Months</td>
                    <td className="p-4 font-bold text-danger-600">
                      <Money paise={d.unpaidPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                    </td>
                    <td className="p-4 font-medium text-warning-500">
                      <Money paise={d.interestFinesPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                    </td>
                    <td className="p-4">
                      <StatusBadge
                        status={d.agingBracket === '90_PLUS_DAYS' ? 'danger' : d.agingBracket === '60_DAYS' ? 'warning' : 'info'}
                        label={d.agingBracket.replace('_', ' ')}
                      />
                    </td>
                    <td className="p-4 font-mono text-xs text-text-secondary">
                      {new Date(d.lastPaymentDate).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
